import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash-es';
import cN from 'classnames';
import { useMediaQuery } from 'react-responsive';
import SlidePage from '@components/Common/SlidePage';
import SlideFooter from '@components/Common/SlideFooter';
import { LightBox } from '@lion-design/core';
import { Cross } from '@lion-design/icons';
import MultiCheckbox from '@components/Common/MultiCheckbox';
import DomainByEnv from '@utils/domainByEnv';
import { rebuildDestionationData } from '@utils/utils';
import { RootState } from '@Redux/search/store';
import { setSendEmitApi, setCityLength } from '@Redux/search/actions';
import { setAllSelected } from '@Redux/search/All/action';
import { setGroupSelected } from '@Redux/search/Group/action';
import { setHotelSelected } from '@Redux/search/Hotel/action';
import { setVocationSelected } from '@Redux/search/Vacation/action';
import { setTicketSelected } from '@Redux/search/Ticket/action';
import { setFlightSelected } from '@Redux/search/Flight/action';
import UseTempData from '../useHook/UseTempData';
import { getApi } from '@utils/fetchAPI';
import styles from '@components/SearchPage/Filters/styles.module.scss';

interface I_ShowDestination {
    checkBoxSelected: any[];
    whichStore: (payload: any) => { type: string; payload: any };
    productType: string;
    initialDestination: any[];
    destinationCount: number;
    showDestination: boolean;
    setShowDestination: React.Dispatch<React.SetStateAction<boolean>>;
    setLightboxDest: (payload: any) => { type: string; payload: any };
}

enum MutiCheckboxKeys {
    // 目的地參數
    CityID = 'CityID',
    CityName = 'CityName',
    CountryID = 'CountryID',
    CountryName = 'CountryName',
}

const ShowAllDestination: React.FC<I_ShowDestination> = ({
    checkBoxSelected,
    whichStore,
    productType,
    destinationCount,
    showDestination,
    setShowDestination,
    setLightboxDest,
}) => {
    // const [showDestination, setShowDestination] = useState(false); // destination光箱開啟
    const [localDestination, setLocalDestination] = useState<any[]>([]); // 開光箱後重新建構的目的地所有資料
    const [keyword, setKeyword] = useState(''); // 開光箱後input的資料
    const [originDestination, setOriginDestination] = useState<any[]>([]); // 目的地最原始的資料
    const [uiDestination, setUiDestination] = useState<any[]>([]); // 畫面呈現的資料可能是全部也可能是關鍵字篩選掉的
    const [sticky, setSticky] = useState(''); // 滾輪滑動反轉固定input
    const [needBorderRadius, setNeedBorderRadius] = useState(true);
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
    const [tempSelectData, selectedState, dispatchStore] = UseTempData([showDestination]);
    const { TagList } = useSelector((state: RootState) => state.search.reducer);
    const dispatch = useDispatch();

    const refInput = useRef<HTMLInputElement>(null);
    const recordY = useRef(0);

    // 針對mobile 滾輪滑動wraper範圍
    const handleScroll = (e: React.MouseEvent<HTMLDivElement>) => {
        const scrollTop = (e.target as HTMLDivElement).scrollTop;
        if (!isMobile) return;
        if (scrollTop < 180) return setSticky(''), setNeedBorderRadius(true);
        if (scrollTop > recordY.current) {
            setSticky('');
            setNeedBorderRadius(false);
        } else {
            setSticky('fixed');
            setNeedBorderRadius(true);
        }

        recordY.current = scrollTop;
    };
    const watchScroll = useCallback(handleScroll, []);

    const handleShowDestination = async () => {
        const getDestination = await getApi(
            `${DomainByEnv()}/json/destination?producttype=${productType}`
        );

        const rebuildData = rebuildDestionationData(
            getDestination,
            MutiCheckboxKeys.CountryName,
            MutiCheckboxKeys.CountryID,
            MutiCheckboxKeys.CityName,
            MutiCheckboxKeys.CityID
        );
        setOriginDestination(() => getDestination);
        setShowDestination(true);
        const recordCityLength: { [key: string]: number } = {};
        const data = Object.entries(rebuildData).map(([key, val]) => {
            recordCityLength[key] = val.values.length + 1; // 因全選的話有手動新增國家的值，故所有城市長度再加1
            return { ParentId: key, ...val };
        });
        dispatch(setLightboxDest({ LightboxDestination: rebuildData }));
        dispatch(setCityLength(recordCityLength));
        setLocalDestination(() => data);
    };
    useEffect(() => {
        if (keyword === '') return setUiDestination(() => localDestination); //關鍵字空的則顯示所有目的地
        const getFilteredDestination = originDestination.filter((val: any) => {
            //原始扁平資料開始找關鍵字相符的城市跟國家
            if (val.CountryName.indexOf(keyword) > -1 || val.CityName.indexOf(keyword) > -1) {
                return val;
            }
        });
        const rebuildData = rebuildDestionationData(
            getFilteredDestination,
            MutiCheckboxKeys.CountryName,
            MutiCheckboxKeys.CountryID,
            MutiCheckboxKeys.CityName,
            MutiCheckboxKeys.CityID
        );
        setUiDestination(() =>
            Object.entries(rebuildData).map(([key, val]) => {
                return { ParentId: key, ...val };
            })
        );
    }, [keyword, localDestination]);
    // 光箱搜尋目的地fn
    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
        console.log(e, 'change event');
        console.log(refInput.current, 'refInput');
        const value = refInput.current?.value;
        if (typeof value === 'string') setKeyword(value);
    };
    const clearInput = () => {
        if (refInput.current != null) {
            (refInput.current as HTMLInputElement).value = '';
        }
        setKeyword('');
    };
    const handledebounce = useCallback(debounce(handleInput, 500), []);
    const controlShow = () => {
        // 關閉光箱、縮回滑動面板回到上一個狀態
        dispatch(dispatchStore(tempSelectData));
        setShowDestination((prev: boolean) => !prev);
    };
    useEffect(() => {
        if (showDestination) {
            //pc跟M版光箱打開時則關閉發送api選項
            dispatch(setSendEmitApi(false));
            return;
        }
        if (!isMobile) {
            // 光箱關閉且是pc版面時
            //pc光箱送出確認送出時即打api，m版目的地光箱送出時暫時不打api
            dispatch(setSendEmitApi(true));
        }
    }, [showDestination]);
    const clearAllFilter = () => {
        switch (
            TagList // 清除篩選條件 不更新排序、產品類型(SortType)
        ) {
            case 'grp':
                return dispatch(
                    setGroupSelected({
                        // 用戶篩選的值
                        selectedDestinationID: [],
                    })
                );
            case 'vac':
                return dispatch(
                    setVocationSelected({
                        selectedDestinationID: [],
                    })
                );
            case 'htl':
                return dispatch(
                    setHotelSelected({
                        selectedDestinationID: [],
                    })
                );
            case 'etk':
                return dispatch(
                    setTicketSelected({
                        selectedDestinationID: [],
                    })
                );
            case 'flt':
                return dispatch(
                    setFlightSelected({
                        selectedDestinationID: [],
                    })
                );
            default:
                return dispatch(
                    setAllSelected({
                        selectedDestinationID: [],
                    })
                );
        }
    };

    return (
        <>
            <div className="showMore" onClick={handleShowDestination}>
                <span>查看全部</span>
            </div>
            {/* M版不能再用光箱包住slidePage，不然fixed兩層下第二層無法知道手機的底部位置，footer在ios會被遮蓋 */}
            {isMobile ? (
                <div className={styles.slidePageWrapper}>
                    <SlidePage title="目的地" visible={showDestination} setIsVisible={controlShow}>
                        <div
                            className={cN(styles.SlideContent, {
                                [`${styles.border_rs_20}`]: sticky === 'fixed' || needBorderRadius,
                            })}
                            onScrollCapture={watchScroll}
                        >
                            <div className={cN(styles.SlideContent_input_wrap)}>
                                <div
                                    className={cN(styles.SlideContent_input, {
                                        [`${styles.fixed}`]: sticky === 'fixed',
                                    })}
                                >
                                    <input
                                        type="text"
                                        className={cN(styles.destinationInput)}
                                        onChange={handledebounce}
                                        defaultValue={keyword}
                                        placeholder="搜尋目的地"
                                        ref={refInput}
                                    />
                                    {keyword && (
                                        <button
                                            className={styles.destinationClear}
                                            type="button"
                                            onClick={clearInput}
                                        >
                                            <Cross type="Cross" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            {/* <hr /> */}
                            <div className={styles.SlideContent_items}>
                                {showDestination && uiDestination.length > 0
                                    ? uiDestination.map((objData, i) => (
                                          <MultiCheckbox
                                              multiData={objData}
                                              checkBoxSelected={checkBoxSelected}
                                              reduxSelectedKey="selectedDestinationID"
                                              whichStore={whichStore}
                                              destinationCount={destinationCount}
                                              lightboxKeyword={keyword}
                                              isDisabled={destinationCount >= 200}
                                              key={'lightboxDest' + i}
                                          />
                                      ))
                                    : '未找到相關目的地'}
                            </div>
                        </div>
                        <SlideFooter
                            onClickLeftBtn={clearAllFilter}
                            onClickRightBtn={() => {
                                if (!isMobile) {
                                    // mobile按確定暫不送
                                    dispatch(setSendEmitApi(true));
                                }
                                dispatch(dispatchStore(selectedState));
                                setShowDestination((prev) => !prev);
                            }}
                            leftBtnText="清除條件"
                            rightBtnText="確定"
                        >
                            <span className={styles.slidePageWrapper_cityCount}>
                                已勾選:{destinationCount}/200
                            </span>
                        </SlideFooter>
                    </SlidePage>
                </div>
            ) : (
                <LightBox
                    className={styles.lightboxDest}
                    open={showDestination}
                    ToggleLightBox={controlShow}
                >
                    <div className={styles.slidePageWrapper}>
                        {/* <SlidePage
                            title="目的地"
                            visible={showDestination}
                            setIsVisible={controlShow}
                            isHeaderShow={false}
                        > */}
                        <div className={cN(styles.SlideContent)} onScrollCapture={watchScroll}>
                            <div className={cN(styles.SlideContent_input_wrap)}>
                                <div className={cN(styles.SlideContent_input)}>
                                    <input
                                        type="text"
                                        className={cN(styles.destinationInput)}
                                        onChange={handledebounce}
                                        defaultValue={keyword}
                                        placeholder="搜尋目的地"
                                        ref={refInput}
                                    />
                                    {keyword && (
                                        <button
                                            className={styles.destinationClear}
                                            type="button"
                                            onClick={clearInput}
                                        >
                                            <Cross type="Cross" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            {/* <hr /> */}
                            <div className={styles.SlideContent_items}>
                                {showDestination && uiDestination.length > 0
                                    ? uiDestination.map((objData, i) => (
                                          <MultiCheckbox
                                              multiData={objData}
                                              checkBoxSelected={checkBoxSelected}
                                              reduxSelectedKey="selectedDestinationID"
                                              whichStore={whichStore}
                                              destinationCount={destinationCount}
                                              lightboxKeyword={keyword}
                                              isDisabled={destinationCount >= 200}
                                              key={'lightboxDest' + i}
                                          />
                                      ))
                                    : '未找到相關目的地'}
                            </div>
                        </div>
                        <SlideFooter
                            onClickLeftBtn={clearAllFilter}
                            onClickRightBtn={() => {
                                if (!isMobile) {
                                    // mobile按確定暫不送
                                    dispatch(setSendEmitApi(true));
                                }
                                dispatch(dispatchStore(selectedState));
                                setShowDestination((prev) => !prev);
                            }}
                            leftBtnText="清除條件"
                            rightBtnText="確定"
                        >
                            <span className={styles.slidePageWrapper_cityCount}>
                                已勾選:{destinationCount}/200
                            </span>
                        </SlideFooter>
                        {/* </SlidePage> */}
                    </div>
                </LightBox>
            )}
        </>
    );
};

export default ShowAllDestination;
